/**
 * PII / secret scrubbing for outgoing error reports.
 *
 * ORION is an end-to-end encrypted messenger, so error reports must never
 * carry plaintext, credentials, or key material off the device. We avoid
 * capturing sensitive fields in the first place (see {@link ./context} and
 * {@link ./breadcrumbs}), and this module is the last line of defense: every
 * free-text field that leaves the client (error message, stack, component
 * stack, breadcrumb text) is passed through {@link scrubText} before send.
 */

const REDACTED = "[redacted]";

type Replacer = { pattern: RegExp; replacement: string };

// Credential patterns — safe to apply everywhere, including stack traces,
// because they don't match ordinary file paths or function names.
const CREDENTIAL_REPLACERS: Replacer[] = [
  // JSON Web Tokens (access / refresh tokens).
  {
    pattern: /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
    replacement: "[jwt]",
  },
  // "Bearer <token>" authorization headers.
  { pattern: /Bearer\s+[A-Za-z0-9._-]+/gi, replacement: "Bearer [redacted]" },
  // Email addresses.
  {
    pattern: /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,
    replacement: "[email]",
  },
];

// Aggressive pattern reserved for free text (messages, breadcrumbs): long
// opaque base64 / hex runs — keys, encrypted blobs, capsules. Intentionally
// NOT applied to stacks, where similar-looking runs are file paths we want to
// keep. Requires base64/hex chars only (no "/" or "-") to avoid eating paths.
// No trailing word-boundary: it would fail to match base64 padding ("=" is a
// non-word char), leaving "==" behind un-redacted.
const OPAQUE_BLOB: Replacer = {
  pattern: /\b[A-Za-z0-9+_]{40,}={0,2}/g,
  replacement: REDACTED,
};

function applyAll(input: string, replacers: Replacer[]): string {
  return replacers.reduce(
    (acc, { pattern, replacement }) => acc.replace(pattern, replacement),
    input,
  );
}

/** Full scrub for free-text fields (error message, breadcrumb text, notes). */
export function scrubText(input: string | null | undefined): string {
  if (!input) {
    return "";
  }
  return applyAll(input, [...CREDENTIAL_REPLACERS, OPAQUE_BLOB]);
}

/**
 * Conservative scrub for code locations (stack, component stack): strips
 * credentials but preserves file paths and frame structure for debugging.
 */
export function scrubCode(
  input: string | null | undefined,
): string | undefined {
  if (!input) {
    return undefined;
  }
  return applyAll(input, CREDENTIAL_REPLACERS);
}
